import { Component, signal, inject, viewChild, ElementRef, PLATFORM_ID, effect, computed } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ThemeService, BackgroundStyle } from './theme';
import { AuthService } from './auth.service';
import { NotificationService } from './notification';
import { PwaService } from './pwa.service';
import { InternetService } from './services/internet.service';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { ActivatedRoute } from '@angular/router';
import { Header } from './header';
import { SwipeTabSyncDirective } from './directives/swipe-tab-sync.directive';
import packageJson from '../../package.json';

@Component({
  selector: 'app-settings',
  imports: [CommonModule, RouterModule, MatIconModule, FormsModule, Header, SwipeTabSyncDirective],
  styles: [`
    @reference "../styles.css";
    .settings-section-header {
      @apply flex items-center gap-6 mb-12;
      animation: ios-slide-up 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    }
    .settings-grid {
      @apply grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8;
    }
    .premium-card {
      @apply bg-white/40 dark:bg-zinc-900/40 p-8 md:p-10 !rounded-[40px] border border-white/20 dark:border-white/10 shadow-ios transition-all duration-500 hover:shadow-ios-lg;
      backdrop-filter: var(--glass-blur);
      -webkit-backdrop-filter: var(--glass-blur);
    }
    
    /* Inset Grouped List Style */
    .ios-list-group {
      @apply bg-white/40 dark:bg-zinc-900/40 rounded-[32px] border border-black/5 dark:border-white/10 overflow-hidden shadow-ios-sm;
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
    }
    .ios-list-item {
      @apply flex items-center justify-between w-full px-8 py-5 transition-all hover:bg-ios-blue/5 border-b border-black/5 dark:border-white/5 last:border-0;
    }
    .ios-list-icon {
      @apply w-12 h-12 rounded-[16px] flex items-center justify-center text-white shadow-ios-sm;
    }
    
    /* Bento Grid Layout */
    .bento-card {
      @apply bg-white/40 dark:bg-zinc-900/40 p-8 rounded-[40px] border border-black/5 dark:border-white/10 shadow-ios transition-all duration-700 hover:shadow-ios-lg h-full flex flex-col relative overflow-hidden;
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
    }
    
    /* Authentic iOS Switch */
    .ios-switch {
      @apply relative inline-flex items-center cursor-pointer;
    }
    .ios-switch input { @apply sr-only; }
    .ios-switch-slider {
      @apply w-[51px] h-[31px] bg-zinc-200 dark:bg-zinc-700/50 rounded-full transition-colors relative duration-300 ring-1 ring-black/5 dark:ring-white/5;
    }
    .ios-switch input:checked + .ios-switch-slider {
      @apply bg-ios-blue shadow-[0_0_15px_rgba(0,122,255,0.3)];
    }
    .ios-switch-slider::after {
      content: '';
      @apply absolute top-[2px] left-[2px] bg-white w-[27px] h-[27px] rounded-full shadow-md transition-transform duration-300 ease-out translate-z-0;
    }
    .ios-switch input:checked + .ios-switch-slider::after {
      @apply translate-x-[20px];
    }
    
    /* Profile Premium Card */
    .profile-card-shimmer {
      @apply relative overflow-hidden;
    }
    .profile-card-shimmer::before {
      content: '';
      @apply absolute -inset-full bg-gradient-to-tr from-transparent via-white/10 to-transparent;
      transform: rotate(45deg);
      animation: profile-shimmer 8s infinite linear;
    }
    @keyframes profile-shimmer {
      0% { transform: scale(3) rotate(45deg) translate(-100%, -100%); }
      100% { transform: scale(3) rotate(45deg) translate(100%, 100%); }
    }
    
    @keyframes ios-slide-up {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .animate-ios-slide {
      animation: ios-slide-up 0.5s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    }
  `],
  template: `
<div class="h-full overflow-y-auto w-full transition-colors duration-700 relative overflow-x-hidden flex flex-col selection:bg-ios-blue/20 bg-transparent">
  
  <!-- Premium Header -->
  <app-header 
    title="Settings" 
    subtitle="@settings" 
    iconName="settings"
    [backLink]="authService.isAuthenticated() ? '/search' : '/'">
    <div actions class="relative z-10 flex items-center gap-3">
      <button (click)="openConfirmModal('signout')" 
              class="text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 rounded-full px-3 py-1.5 flex items-center gap-1 text-sm font-medium transition-colors border border-red-200 dark:border-red-500/20 shadow-sm active:scale-95">
        <mat-icon class="!text-lg !w-[18px] !h-[18px]">logout</mat-icon>
        <span>Sign Out</span>
      </button>
    </div>
  </app-header>

  <!-- Liquified Navigation -->
  <div class="ios-sub-header-liquid" #sidebar>
    <div class="chip-scroll-container !gap-4" #settingsChips>
      @for (item of menuItems; track item.id) {
        @let isActive = activeSection() === item.id;
        <button 
          (click)="onMenuSelect(item.id)"
          [id]="'menu-item-' + item.id"
          class="ios-liquid-chip"
          [class.active]="isActive"
          [style.background-color]="isActive ? item.color : ''"
          [style.box-shadow]="isActive ? '0 4px 12px ' + item.color + '4D' : ''"
        >
          <mat-icon class="!text-[14px]">{{ item.icon }}</mat-icon>
          <span>{{ item.label }}</span>
        </button>
      }
    </div>
  </div>

  <div 
    class="max-w-7xl mx-auto flex flex-col flex-1 pt-[176px] px-6 md:px-12 relative z-10 w-full animate-ios-fade"
    appSwipeTabSync
    [tabs]="menuItems"
    [activeTab]="activeSection()"
    [chipContainer]="settingsChips"
    chipIdPrefix="menu-item-"
    (activeTabChange)="onMenuSelect($event)"
  >
    
    <!-- Content -->
    <main class="flex-1 py-4 md:py-8 w-full">
      @switch (activeSection()) {
                @case ('pwa') {
          <section class="animate-ios-slide space-y-12 pb-24">
            <div class="settings-section-header !mb-12">
              <div class="w-12 h-12 md:w-14 md:h-14 bg-ios-purple/10 rounded-2xl flex items-center justify-center text-ios-purple ring-1 ring-ios-purple/20 shrink-0">
                <mat-icon class="!text-2xl md:!text-3xl text-ios-purple">settings</mat-icon>
              </div>
              <div>
                <h2 class="text-xl md:text-2xl font-semibold tracking-tight text-ios-title mb-0.5">App Settings</h2>
                <p class="text-[13px] text-gray-500">Appearance and application settings</p>
              </div>
            </div>

            <!-- Theme Compact UI -->
            <div class="space-y-6">
              <h4 class="px-8 text-[13px] font-semibold text-gray-400 uppercase tracking-wider">Theme</h4>
              <div class="ios-list-group shadow-ios-sm !p-6 border-black/5 dark:border-white/5">
                 <div class="grid grid-cols-3 gap-3">
                   @for (theme of themes; track theme.id) {
                     @let isThemeActive = themeService.theme() === theme.id;
                     <button 
                       (click)="onThemeChange(theme.id)"
                       class="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl transition-all border relative bg-black/5 dark:bg-white/5"
                       [class.border-ios-blue]="isThemeActive"
                       [class.bg-ios-blue/10]="isThemeActive"
                       [class.text-ios-blue]="isThemeActive"
                       [class.border-transparent]="!isThemeActive"
                       [class.hover:bg-black/10]="!isThemeActive"
                       [class.dark:hover:bg-white/10]="!isThemeActive"
                     >
                        <mat-icon class="!text-2xl">{{ theme.icon }}</mat-icon>
                        <span class="text-[10px] font-bold tracking-tight">{{ theme.label }}</span>
                     </button>
                   }
                 </div>
              </div>
            </div>

            <!-- Background Compact UI -->
            <div class="space-y-6">
               <h4 class="px-8 text-[13px] font-semibold text-gray-400 uppercase tracking-wider">Background</h4>
               <div class="ios-list-group shadow-ios-sm !p-6 border-black/5 dark:border-white/5">
                  <div class="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-8 gap-3">
                    @for (style of themeService.backgroundThemes; track style.id) {
                      @let isStyleActive = themeService.backgroundStyle() === style.id;
                      <button 
                        (click)="onBackgroundChange(style.id)"
                        [disabled]="style.id !== 'india'"
                        class="group flex flex-col items-center justify-center gap-3 p-3 border rounded-2xl transition-all relative overflow-hidden bg-black/5 dark:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                        [class.active:scale-95]="style.id === 'india'"
                        [class.border-ios-blue]="isStyleActive"
                        [class.bg-ios-blue/10]="isStyleActive"
                        [class.border-transparent]="!isStyleActive"
                        [class.hover:bg-black/10]="style.id === 'india' && !isStyleActive"
                        [class.dark:hover:bg-white/10]="style.id === 'india' && !isStyleActive"
                        title="{{ style.label }}"
                      >
                        <div class="flex -space-x-2">
                          @for (color of style.colors; track $index) {
                            <div class="w-6 h-6 rounded-full shadow-sm ring-2 ring-white dark:ring-zinc-900 overflow-hidden group-hover:scale-110 transition-transform duration-500" [class]="color"></div>
                          }
                        </div>
                      </button>
                    }
                  </div>
               </div>
            </div>

            <!-- Language Section UI -->
            <div class="space-y-6">
              <h4 class="px-8 text-[13px] font-semibold text-gray-400 uppercase tracking-wider">Language</h4>
              <div class="ios-list-group shadow-ios-sm !p-6 border-black/5 dark:border-white/5">
                <div class="flex items-center justify-between p-4 rounded-2xl bg-ios-blue/10 border border-ios-blue/30 text-ios-blue">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-xl bg-ios-blue text-white flex items-center justify-center font-bold text-xs shadow-sm">
                      EN
                    </div>
                    <div>
                      <span class="font-bold text-sm block text-ios-title dark:text-white">English</span>
                      <span class="text-xs text-gray-500">System Language</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notifications Toggle UI -->
            <div class="space-y-6">
              <h4 class="px-8 text-[13px] font-semibold text-gray-400 uppercase tracking-wider">Notifications</h4>
              <div class="ios-list-group shadow-ios-sm !p-6 border-black/5 dark:border-white/5">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-4">
                    <div class="w-10 h-10 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                      <mat-icon class="!text-xl">notifications</mat-icon>
                    </div>
                    <div>
                      <div class="text-[15px] font-semibold text-ios-title">Notifications</div>
                      <div class="text-[12px] text-gray-500">Enable in-app updates and alerts</div>
                    </div>
                  </div>
                  <label class="ios-switch">
                    <input 
                      type="checkbox" 
                      [checked]="notificationsEnabled()" 
                      (change)="onNotificationToggle($event)"
                    />
                    <span class="ios-switch-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Advanced PWA Installation & Capabilities Bento Card -->
            <div class="bento-card !p-8 md:!p-10 relative overflow-hidden group">
               <div class="absolute -left-32 -bottom-32 w-96 h-96 bg-ios-blue/15 rounded-full blur-[110px] animate-liquid pointer-events-none"></div>
               
               <div class="flex flex-col md:flex-row items-center gap-8 md:gap-10 relative z-10">
                  <div class="w-28 h-28 md:w-32 md:h-32 bg-white dark:bg-[#181822] rounded-[32px] flex items-center justify-center shadow-ios-xl shadow-black/10 dark:shadow-white/5 relative overflow-hidden shrink-0 group-hover:scale-105 transition-all duration-700 p-3 border border-black/5 dark:border-white/10">
                    <img src="kicgle.svg" alt="Kicgle Logo" class="w-full h-full object-contain p-2">
                    <div class="absolute inset-0 bg-gradient-to-tr from-black/5 to-transparent pointer-events-none rounded-[32px]"></div>
                  </div>

                  <div class="flex-1 text-center md:text-left space-y-4">
                    <div class="flex flex-wrap items-center justify-center md:justify-start gap-2">
                      <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue text-[11px] font-black uppercase tracking-wider border border-ios-blue/25">
                        <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        {{ pwaService.platformName() }}
                      </span>
                      @if (pwaService.isInstalled()) {
                        <span class="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold border border-emerald-500/30">
                          <mat-icon class="!text-[14px]">check_circle</mat-icon> Standalone Mode
                        </span>
                      }
                    </div>

                    <div class="space-y-1">
                      <h3 class="text-2xl font-bold tracking-tight text-ios-title">
                        {{ pwaService.isInstalled() ? 'Kicgle App Installed' : 'Install Kicgle App' }}
                      </h3>
                      <p class="text-[14px] opacity-70 leading-relaxed text-gray-500 dark:text-zinc-400">
                        {{ pwaService.isInstalled()
                          ? 'Kicgle is installed as a high-performance standalone Progressive Web App with local caching and instant launch.'
                          : 'Install Kicgle on your home screen or desktop for 0ms startup, offline resilience, and a distraction-free window.' }}
                      </p>
                    </div>
                    
                    <div class="pt-2 flex flex-wrap items-center justify-center md:justify-start gap-3">
                       @if (!pwaService.isInstalled()) {
                         <button 
                           type="button"
                           (click)="pwaService.installApp()" 
                           class="h-11 px-8 rounded-full bg-ios-blue text-white font-black text-xs uppercase tracking-wider shadow-ios shadow-ios-blue/25 hover:bg-ios-blue/90 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                         >
                           <mat-icon class="!text-[18px]">{{ pwaService.isInstallable() ? 'download' : 'install_mobile' }}</mat-icon>
                           <span>{{ pwaService.isInstallable() ? 'Install App' : 'Add to Device' }}</span>
                         </button>
                         <button
                           type="button"
                           (click)="pwaService.openInstallModal()"
                           class="h-11 px-6 rounded-full bg-black/5 dark:bg-white/10 text-slate-700 dark:text-zinc-200 font-bold text-xs uppercase tracking-wider hover:bg-black/10 dark:hover:bg-white/15 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer border border-black/5 dark:border-white/10"
                         >
                           <mat-icon class="!text-[18px]">info</mat-icon>
                           <span>Install Guide</span>
                         </button>
                       } @else {
                         <button
                           type="button"
                           (click)="pwaService.openInstallModal()"
                           class="h-11 px-8 rounded-full bg-emerald-600 text-white font-bold text-xs uppercase tracking-wider shadow-ios shadow-emerald-600/25 hover:bg-emerald-700 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                         >
                           <mat-icon class="!text-[18px]">tune</mat-icon>
                           <span>PWA Engine Diagnostics</span>
                         </button>
                       }
                    </div>
                  </div>
               </div>
            </div>

            <!-- Future PWA Engine & Storage Diagnostics Grid -->
            <div class="space-y-6">
               <h4 class="px-8 text-[13px] font-semibold text-gray-400 uppercase tracking-wider">Storage & Hardware Engine</h4>
               <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Storage Quota Gauge -->
                  <div class="bento-card !p-6 space-y-3">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm">
                        <mat-icon class="text-ios-blue !text-[20px]">storage</mat-icon>
                        <span>Offline Storage Quota</span>
                      </div>
                      <span class="text-xs font-mono text-slate-500 dark:text-zinc-400">
                        {{ pwaService.storageInfo().usageMB }} MB / {{ pwaService.storageInfo().quotaMB }} MB
                      </span>
                    </div>

                    <div class="w-full h-2.5 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
                      <div
                        class="h-full bg-gradient-to-r from-ios-blue via-ios-purple to-emerald-500 rounded-full transition-all duration-500"
                        [style.width.%]="pwaService.storageInfo().usagePercent || 1"
                      ></div>
                    </div>

                    <div class="flex items-center justify-between text-xs pt-1">
                      <span class="text-[11px] text-slate-500 dark:text-zinc-400">
                        {{ pwaService.storageInfo().usagePercent }}% allocated
                      </span>
                      <button
                        type="button"
                        (click)="pwaService.clearOfflineCache()"
                        class="text-[11px] font-black text-red-500 hover:text-red-600 uppercase tracking-wider cursor-pointer"
                      >
                        Purge Storage
                      </button>
                    </div>
                  </div>

                  <!-- Future PWA APIs -->
                  <div class="bento-card !p-6 space-y-3">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm">
                        <mat-icon class="text-purple-500 !text-[20px]">bolt</mat-icon>
                        <span>Next-Gen Web APIs</span>
                      </div>
                      <span class="text-[10px] font-mono font-bold uppercase text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                        Modern Standard
                      </span>
                    </div>

                    <div class="grid grid-cols-2 gap-2 text-xs">
                      <div class="p-2 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-between">
                        <span class="text-[11px] font-semibold text-slate-700 dark:text-zinc-300">Badging</span>
                        <span class="font-mono text-[10px] font-bold" [class.text-emerald-500]="pwaService.hasBadging()">
                          {{ pwaService.hasBadging() ? 'Supported' : 'Unavailable' }}
                        </span>
                      </div>
                      <div class="p-2 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-between">
                        <span class="text-[11px] font-semibold text-slate-700 dark:text-zinc-300">Wake Lock</span>
                        <span class="font-mono text-[10px] font-bold" [class.text-emerald-500]="pwaService.hasWakeLock()">
                          {{ pwaService.hasWakeLock() ? 'Supported' : 'Unavailable' }}
                        </span>
                      </div>
                      <div class="p-2 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-between">
                        <span class="text-[11px] font-semibold text-slate-700 dark:text-zinc-300">Web Share</span>
                        <span class="font-mono text-[10px] font-bold text-emerald-500">Supported</span>
                      </div>
                      <div class="p-2 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-between">
                        <span class="text-[11px] font-semibold text-slate-700 dark:text-zinc-300">Window Overlay</span>
                        <span class="font-mono text-[10px] font-bold text-emerald-500">Declared</span>
                      </div>
                    </div>
                  </div>
               </div>
            </div>

            <!-- Updates & Cache -->
            <div class="space-y-6">
               <h4 class="px-8 text-[13px] font-semibold text-gray-400 uppercase tracking-wider">Updates & Cache</h4>
               <div class="ios-list-group shadow-ios-sm border-black/5 dark:border-white/5">
                  <div class="ios-list-item flex-col md:flex-row !items-start md:!items-center gap-4 !py-6">
                     <div class="flex items-center gap-5 w-full md:w-auto">
                        <div class="ios-list-icon bg-ios-blue shadow-ios shadow-ios-blue/20">
                           <mat-icon class="!text-xl">sync</mat-icon>
                        </div>
                        <div class="flex flex-col flex-1">
                           <p class="text-[17px] font-medium tracking-tight text-ios-title">App Version</p>
                           <p class="text-[15px] font-mono tracking-tight mt-1">{{ appVersion() }}</p>
                           <p class="text-xs text-gray-500 mt-1">{{ lastUpdated() }}</p>
                        </div>
                     </div>
                     <div class="flex items-center gap-3 w-full md:w-auto justify-end">
                       <button
                         type="button"
                         (click)="pwaService.checkForUpdates()"
                         class="ios-liquid-chip !bg-ios-blue/10 !text-ios-blue !border-ios-blue/20 !px-4 !py-2 hover:bg-ios-blue hover:text-white transition-all cursor-pointer"
                       >
                         <mat-icon class="!text-[16px]">refresh</mat-icon>
                         <span class="text-[12px] font-medium">Check Updates</span>
                       </button>
                     </div>
                  </div>
               </div>
            </div>

          </section>
        }
        @case ('internet') {
          <section class="animate-ios-slide space-y-12 pb-24">
            <div class="settings-section-header !mb-12">
              <div class="w-12 h-12 md:w-14 md:h-14 bg-ios-blue/10 rounded-2xl flex items-center justify-center text-ios-blue ring-1 ring-ios-blue/20 shrink-0">
                <mat-icon class="!text-2xl md:!text-3xl text-ios-blue">public</mat-icon>
              </div>
              <div>
                <h2 class="text-xl md:text-2xl font-semibold tracking-tight text-ios-title mb-0.5">Internet</h2>
                <p class="text-[13px] text-gray-500">Connection stats and network health</p>
              </div>
            </div>

            <div class="flex flex-col gap-8">
              <div class="bento-card !p-12 relative overflow-hidden group">
                 <div class="absolute -right-20 -top-20 w-80 h-80 bg-ios-blue/15 rounded-full blur-[90px] animate-liquid"></div>
                 <div class="flex items-center justify-between mb-12 relative z-10">
                   <h3 class="text-xl md:text-2xl font-bold tracking-tight text-ios-title flex items-center gap-4">
                     <mat-icon class="text-ios-blue !text-3xl">speed</mat-icon> Internet Speed
                   </h3>
                   <div class="ios-liquid-chip !text-ios-green !bg-ios-green/10 !border-ios-green/20 animate-pulse">
                     <div class="w-2 h-2 rounded-full bg-ios-green"></div>
                     <span>Online</span>
                   </div>
                 </div>

                 <div class="grid grid-cols-1 md:grid-cols-2 gap-16 relative z-10">
                    <div class="space-y-6">
                        <span class="text-[12px] font-semibold uppercase tracking-wider text-gray-500">Download Speed</span>
                        <div class="flex items-end gap-2">
                           <span class="text-4xl md:text-5xl font-bold tracking-tighter text-ios-title leading-none">{{ (internetService.speed() / 8) | number:'1.2-2' }}</span>
                           <span class="text-[13px] font-semibold text-gray-400 mb-2 uppercase">MB/s</span>
                        </div>
                       <div class="h-2 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden border border-black/5 dark:border-white/5">
                          <div class="h-full bg-ios-blue rounded-full transition-all duration-[2000ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]" [style.width.%]="((internetService.speed() / 8) / 100) * 100"></div>
                       </div>
                    </div>
                    <div class="space-y-6">
                        <span class="text-[12px] font-semibold uppercase tracking-wider text-gray-500">Ping Address</span>
                        <div class="flex items-end gap-2">
                           <span class="text-6xl font-bold tracking-tighter text-ios-title leading-none">24</span>
                           <span class="text-[13px] font-semibold text-gray-400 mb-2 uppercase">ms</span>
                        </div>
                       <div class="h-2 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden border border-black/5 dark:border-white/5">
                          <div class="h-full bg-ios-purple rounded-full transition-all duration-[2000ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]" [style.width.%]="40"></div>
                       </div>
                    </div>
                 </div>
              </div>

              <div class="flex flex-col gap-6 w-full">
                 <h3 class="text-xl md:text-2xl font-bold tracking-tight text-ios-title px-4">Network Details</h3>
                 <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                   <div class="p-6 rounded-3xl bg-ios-blue/5 border border-ios-blue/10 flex flex-col gap-3">
                     <div class="flex items-center gap-2 text-ios-blue">
                       <mat-icon class="!text-xl">language</mat-icon>
                       <span class="text-[12px] font-semibold uppercase tracking-wider">IP Address</span>
                     </div>
                     <span class="text-sm sm:text-base font-bold text-ios-title break-all leading-tight">{{ networkIp() }}</span>
                   </div>
                   
                   <div class="p-6 rounded-3xl bg-ios-purple/5 border border-ios-purple/10 flex flex-col gap-3">
                     <div class="flex items-center gap-2 text-ios-purple">
                       <mat-icon class="!text-xl">fingerprint</mat-icon>
                       <span class="text-[12px] font-semibold uppercase tracking-wider">MAC Address</span>
                     </div>
                     <span class="text-sm sm:text-base font-bold text-ios-title break-all leading-tight">N/A (Restricted)</span>
                   </div>

                   <div class="p-6 rounded-3xl bg-ios-orange/5 border border-ios-orange/10 flex flex-col gap-3">
                     <div class="flex items-center gap-2 text-ios-orange">
                       <mat-icon class="!text-xl">business</mat-icon>
                       <span class="text-[12px] font-semibold uppercase tracking-wider">Network Provider</span>
                     </div>
                     <span class="text-sm sm:text-base font-bold text-ios-title break-words leading-tight">{{ networkIsp() }}</span>
                   </div>

                   <div class="p-6 rounded-3xl bg-ios-green/5 border border-ios-green/10 flex flex-col gap-3">
                     <div class="flex items-center gap-2 text-ios-green">
                       <mat-icon class="!text-xl">router</mat-icon>
                       <span class="text-[12px] font-semibold uppercase tracking-wider">Connection Type</span>
                     </div>
                     <span class="text-sm sm:text-base font-bold text-ios-title break-all leading-tight">{{ pwaService.connectionSpeed() }}</span>
                   </div>

                   <div class="p-6 rounded-3xl bg-ios-teal/5 border border-ios-teal/10 flex flex-col gap-3">
                     <div class="flex items-center gap-2 text-ios-teal">
                       <mat-icon class="!text-xl">swap_vert</mat-icon>
                       <span class="text-[12px] font-semibold uppercase tracking-wider">Bandwidth</span>
                     </div>
                     <span class="text-sm sm:text-base font-bold text-ios-title break-all leading-tight">{{ pwaService.networkStats().downlink }} Mbps</span>
                   </div>

                   <div class="p-6 rounded-3xl bg-ios-yellow/5 border border-ios-yellow/10 flex flex-col gap-3">
                     <div class="flex items-center gap-2 text-ios-yellow">
                       <mat-icon class="!text-xl">timer</mat-icon>
                       <span class="text-[12px] font-semibold uppercase tracking-wider">Latency</span>
                     </div>
                     <span class="text-sm sm:text-base font-bold text-ios-title break-all leading-tight">{{ pwaService.networkStats().rtt }} ms</span>
                   </div>
                 </div>

                 <button (click)="pulseCheck()" [disabled]="isPulseChecking()" class="w-full md:w-auto self-center md:self-end px-12 h-14 bg-ios-blue text-white rounded-2xl text-[12px] font-bold uppercase tracking-[0.3em] shadow-ios-lg shadow-ios-blue/20 hover:scale-[1.02] active:scale-95 transition-all mt-2 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-3">
                   @if (isPulseChecking()) {
                     <mat-icon class="animate-spin !text-lg">refresh</mat-icon>
                     Testing...
                   } @else {
                     <mat-icon class="!text-lg">network_check</mat-icon>
                     Pulse Check
                   }
                 </button>
              </div>
            </div>

            <!-- Usage Metrics Overview -->
            <div class="space-y-8 pt-6">
                <div class="flex items-center justify-between px-8">
                  <h4 class="ios-sub-header">Consumption Chronology</h4>
                  <div class="flex items-center gap-1 bg-black/5 dark:bg-white/5 p-1 rounded-xl text-[10px] font-black border border-black/5 dark:border-white/5 overflow-x-auto whitespace-nowrap scroll-smooth max-w-[200px] sm:max-w-none">
                     @for (period of ['daily', 'monthly', 'yearly']; track period) {
                       @let isPeriodActive = selectedPeriod() === period;
                       <button (click)="selectedPeriod.set($any(period))" 
                               [class.bg-white]="isPeriodActive" [class.dark:bg-zinc-800]="isPeriodActive"
                               [class.text-ios-blue]="isPeriodActive" [class.shadow-ios-sm]="isPeriodActive"
                               class="px-4 py-2 rounded-lg uppercase tracking-widest transition-all shrink-0">
                         {{ period }}
                       </button>
                     }
                  </div>
               </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                 <div class="bento-card !p-8 relative overflow-hidden group">
                   <div class="absolute -right-10 -top-10 w-32 h-32 bg-ios-blue/20 rounded-full blur-[40px] animate-liquid"></div>
                   <span class="text-[11px] font-black uppercase tracking-[0.3em] opacity-40 mb-3 block relative z-10">Total Extracted</span>
                   <div class="flex items-end gap-1 relative z-10 break-words flex-wrap">
                     <span class="text-3xl lg:text-4xl font-bold text-ios-title tracking-tighter">{{ currentMetrics().total | number:'1.2-2' }}</span>
                     <span class="text-sm font-bold opacity-40 pb-1 tracking-widest text-gray-500 uppercase">GB</span>
                   </div>
                 </div>

                 <div class="bento-card !p-8 relative overflow-hidden group">
                   <div class="absolute -right-10 -top-10 w-32 h-32 bg-ios-green/20 rounded-full blur-[40px] animate-liquid" style="animation-delay: -1s"></div>
                   <span class="text-[11px] font-black uppercase tracking-[0.3em] opacity-40 mb-3 block relative z-10">Inbound Flow</span>
                   <div class="flex items-end gap-1 relative z-10 break-words flex-wrap">
                     <span class="text-3xl lg:text-4xl font-bold text-ios-title tracking-tighter">{{ currentMetrics().download | number:'1.2-2' }}</span>
                     <span class="text-sm font-bold opacity-40 pb-1 tracking-widest text-gray-500 uppercase">GB</span>
                   </div>
                 </div>

                 <div class="bento-card !p-8 relative overflow-hidden group">
                   <div class="absolute -right-10 -top-10 w-32 h-32 bg-ios-orange/20 rounded-full blur-[40px] animate-liquid" style="animation-delay: -2s"></div>
                   <span class="text-[11px] font-black uppercase tracking-[0.3em] opacity-40 mb-3 block relative z-10">Outbound Flow</span>
                   <div class="flex items-end gap-1 relative z-10 break-words flex-wrap">
                     <span class="text-3xl lg:text-4xl font-bold text-ios-title tracking-tighter">{{ currentMetrics().upload | number:'1.2-2' }}</span>
                     <span class="text-sm font-bold opacity-40 pb-1 tracking-widest text-gray-500 uppercase">GB</span>
                   </div>
                 </div>
              </div>

               <div class="ios-list-group !rounded-[40px] overflow-hidden border-black/5 dark:border-white/5">
                  <div class="max-h-[480px] overflow-y-auto divide-y divide-black/5 dark:divide-white/5 no-scrollbar">
                    @for (day of internetService.dailyUsage(); track day.date) {
                      <div class="p-8 flex items-center justify-between hover:bg-ios-blue/5 transition-all group cursor-default">
                         <div class="flex flex-col">
                           <span class="text-[17px] font-bold text-ios-title tracking-tight">{{ day.date | date:'EEEE, MMM d' }}</span>
                           <div class="flex items-center gap-3 mt-1">
                             <div class="w-1.5 h-1.5 rounded-full bg-ios-green"></div>
                             <span class="text-[11px] font-black opacity-30 uppercase tracking-widest">Shard Integrity: Optimal</span>
                           </div>
                         </div>
                         <div class="flex items-center gap-10">
                            <div class="flex flex-col items-end">
                               <span class="text-[16px] font-black text-ios-title">{{ day.download + day.upload | number:'1.2-2' }} <span class="text-[10px] opacity-30">GB</span></span>
                               <div class="flex items-center gap-2 mt-1">
                                  <mat-icon class="!text-[10px] text-ios-green">south_west</mat-icon>
                                  <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">{{ day.download | number:'1.1-1' }}</span>
                                  <mat-icon class="!text-[10px] text-ios-orange">north_east</mat-icon>
                                  <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">{{ day.upload | number:'1.1-1' }}</span>
                               </div>
                            </div>
                            <mat-icon class="text-ios-blue opacity-0 group-hover:opacity-40 transition-all duration-500 group-hover:translate-x-1">chevron_right</mat-icon>
                         </div>
                      </div>
                    }
                  </div>
               </div>
            </div>
          </section>
        }
      }
    </main>
  </div>
      <!-- Custom Confirmation Modal -->
      @if (modal.isOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-6 sm:p-12 animate-ios-fade">
          <div class="absolute inset-0 bg-black/60 backdrop-blur-2xl transition-all duration-700" (click)="closeModal()" (keyup.enter)="closeModal()" tabindex="0" aria-label="Close modal"></div>
          <div class="bento-card !bg-white/85 dark:!bg-zinc-900/85 w-full max-w-md p-10 sm:p-12 relative z-10 animate-ios-slide shadow-ios-2xl border-white/40 overflow-hidden">
            <div class="absolute -right-20 -top-20 w-48 h-48 bg-ios-blue/10 rounded-full blur-[60px] animate-liquid"></div>
            
            @if (isScanning()) {
              <div class="flex flex-col items-center justify-center py-12 space-y-12 animate-ios-fade relative z-10">
                <div class="relative w-44 h-44">
                  <div class="absolute inset-0 border-4 border-ios-blue/10 rounded-full"></div>
                  <div class="absolute inset-0 border-4 border-ios-blue border-t-transparent rounded-full animate-[spin_2s_linear_infinite]"></div>
                  <div class="absolute inset-0 flex items-center justify-center">
                    <mat-icon class="text-ios-blue !text-[72px] w-[72px] h-[72px] animate-pulse">fingerprint</mat-icon>
                  </div>
                  <!-- Scanning line -->
                  <div class="absolute top-0 left-0 right-0 h-2 bg-ios-blue/50 blur-[4px] animate-[scan_2.5s_ease-in-out_infinite]"></div>
                </div>
                <div class="text-center space-y-3">
                  <h3 class="text-3xl font-black tracking-tighter text-ios-title">Verifying Identity</h3>
                  <p class="text-[11px] font-black opacity-30 uppercase tracking-[0.4em]">Nodal Biometric Handshake</p>
                </div>
              </div>
            } @else {
              <div class="flex items-center gap-6 mb-10 relative z-10">
                <div class="w-16 h-16 rounded-[22px] flex items-center justify-center shadow-ios-sm relative" 
                     [class.bg-ios-blue/10]="modal.type() !== 'signout' && modal.type() !== 'verify' && modal.type() !== 'biometric'" 
                     [class.bg-ios-red/10]="modal.type() === 'signout'" 
                     [class.bg-ios-yellow/10]="modal.type() === 'verify'" 
                     [class.bg-ios-purple/10]="modal.type() === 'biometric'">
                  <div class="absolute inset-0 rounded-[22px] animate-pulse opacity-50"
                       [class.bg-ios-blue/10]="modal.type() !== 'signout' && modal.type() !== 'verify' && modal.type() !== 'biometric'" 
                       [class.bg-ios-red/10]="modal.type() === 'signout'" 
                       [class.bg-ios-yellow/10]="modal.type() === 'verify'" 
                       [class.bg-ios-purple/10]="modal.type() === 'biometric'"></div>
                  <mat-icon class="!text-3xl relative z-10"
                           [class.text-ios-blue]="modal.type() !== 'signout' && modal.type() !== 'verify' && modal.type() !== 'biometric'" 
                           [class.text-ios-red]="modal.type() === 'signout'" 
                           [class.text-ios-green]="modal.type() === 'verify' || modal.type() === 'biometric'">
                    {{ modal.type() === 'email' ? 'alternate_email' : modal.type() === 'password' ? 'lock_reset' : modal.type() === 'signout' ? 'logout' : modal.type() === 'biometric' ? 'fingerprint' : 'verified_user' }}
                  </mat-icon>
                </div>
                <div>
                    <h3 class="text-2xl font-black tracking-tight text-ios-title leading-tight">{{ modal.title() }}</h3>
                    <div class="h-1 w-12 bg-ios-blue/20 rounded-full mt-2"></div>
                </div>
              </div>
              
              <p class="text-[16px] font-medium mb-12 opacity-60 leading-relaxed whitespace-pre-wrap relative z-10 italic">{{ modal.message() }}</p>

              @if (modal.type() === 'email') {
                <div class="space-y-3 mb-10 relative z-10">
                  <label for="newEmail" class="text-[11px] font-black uppercase tracking-[0.3em] opacity-40 px-2 shrink-0">New Vector Address</label>
                  <div class="ios-input-container !h-[64px] !rounded-2xl border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5">
                    <input 
                      id="newEmail"
                      type="email" 
                      [value]="modalInput()"
                      (input)="modalInput.set($any($event.target).value)"
                      placeholder="vector@node_01.kicgle.com"
                      class="ios-input !text-[17px] font-bold tracking-tight"
                    >
                  </div>
                </div>
              }

              <div class="flex gap-5 relative z-10">
                <button (click)="closeModal()" class="flex-1 h-16 rounded-2xl font-black text-[12px] uppercase tracking-[0.2em] bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-all border border-black/5 dark:border-white/5 text-ios-title">
                  Dismiss
                </button>
                <button 
                  (click)="confirmAction()" 
                  class="flex-1 h-16 rounded-2xl font-black text-[12px] uppercase tracking-[0.2em] text-white transition-all shadow-ios-lg active:scale-[0.95]"
                  [class.bg-ios-blue]="modal.type() !== 'signout' && modal.type() !== 'verify' && modal.type() !== 'biometric'"
                  [class.bg-ios-red]="modal.type() === 'signout'"
                  [class.bg-ios-yellow]="modal.type() === 'verify'"
                  [class.bg-ios-purple]="modal.type() === 'biometric'"
                  [class.shadow-ios-blue/30]="modal.type() !== 'signout' && modal.type() !== 'verify' && modal.type() !== 'biometric'"
                  [class.shadow-ios-red/30]="modal.type() === 'signout'"
                  [class.shadow-ios-yellow/30]="modal.type() === 'verify'"
                  [class.shadow-ios-purple/30]="modal.type() === 'biometric'"
                >
                   Execute
                </button>
              </div>
            }
          </div>
        </div>
      }

    </div>
  `,
})
export class SettingsView {
  networkIp = signal<string>('Fetching...');
  networkIsp = signal<string>('Fetching...');
  
  fetchNetworkData = async () => {
    try {
      const res = await fetch('https://ipapi.co/json/');
      const data = await res.json();
      if(data.ip) this.networkIp.set(data.ip);
      if(data.org || data.isp) this.networkIsp.set(data.org || data.isp);
    } catch {
      this.networkIp.set('Unknown');
      this.networkIsp.set('Unknown');
    }
  };

    getFormattedDate(date: unknown): string {
    if (!date) return 'N/A';
    const d = date as { toDate?: () => Date };
    let parsedDate = date;
    if (d && typeof d.toDate === 'function') parsedDate = d.toDate();
    if (parsedDate instanceof Date && !isNaN(parsedDate.valueOf())) {
      return parsedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return String(parsedDate);
  }









  appVersion = signal<string>(packageJson.version);
  
  getTimeAgo(versionString: string): string {
    if (!versionString) return 'Unknown';
    const parts = versionString.split('.');
    if (parts.length !== 6) return 'Unknown';
    
    const [year, month, day, hour, min, sec] = parts.map(Number);
    const date = new Date(year, month - 1, day, hour, min, sec);
    const diff = new Date().getTime() - date.getTime();
    
    if (diff < 0) return 'Updated just now';
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Updated just now';
    if (hours < 1) return `Updated ${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `Updated ${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (hours < 48) return 'Updated yesterday';
    return `Updated ${days} day${days !== 1 ? 's' : ''} ago`;
  }

  lastUpdated = computed(() => this.getTimeAgo(this.appVersion()));

  themeService = inject(ThemeService);
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  pwaService = inject(PwaService);
  internetService = inject(InternetService);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  activeSection = signal('pwa');
  selectedSafeSearch = 'filter';
  autoRenewal = signal(true);
  biometricEnabled = signal(false);
  isScanning = signal(false);
  sidebar = viewChild<ElementRef>('sidebar');

  notificationsEnabled = signal<boolean>(true);
  selectedLanguage = signal<string>('English');

  selectedPeriod = signal<'daily' | 'monthly' | 'yearly'>('daily');

  currentMetrics = computed(() => {
    const usages = this.internetService.dailyUsage();
    let computedBytes = 0;

    const todayStr = new Date().toISOString().split('T')[0];
    
    if (this.selectedPeriod() === 'daily') {
      const today = usages.find(u => u.date === todayStr);
      computedBytes = today ? today.bytes : 0;
    } else if (this.selectedPeriod() === 'monthly') {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      computedBytes = usages.filter(u => {
        const d = new Date(u.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      }).reduce((acc, curr) => acc + curr.bytes, 0);
      computedBytes = Math.max(computedBytes, this.internetService.totalUsage() * 0.3);
    } else {
      const currentYear = new Date().getFullYear();
      computedBytes = usages.filter(u => {
        const d = new Date(u.date);
        return d.getFullYear() === currentYear;
      }).reduce((acc, curr) => acc + curr.bytes, 0);
      computedBytes = Math.max(computedBytes, this.internetService.totalUsage() * 0.8);
    }

    const totalGb = computedBytes / (1024 * 1024 * 1024);
    return {
      total: totalGb,
      download: totalGb * 0.8,
      upload: Math.max(0.001, totalGb * 0.2)
    };
  });

  constructor() {
    this.fetchNetworkData();   
    effect(() => {
      const section = this.activeSection();
      const user = this.authService.user();
      
      // Auto-scroll to active tab
      this.scrollToActive(section);

      if (user && this.isBrowser) {
        this.loadUserSettings(user.uid);
      }
    });
  }

  async onThemeChange(themeId: 'light' | 'dark' | 'system' | 'auto') {
    this.themeService.setTheme(themeId);
    await this.persistSettings({
      Theme: themeId,
      theme: themeId
    });
  }

  async onBackgroundChange(styleId: string) {
    this.themeService.setBackgroundStyle(styleId as BackgroundStyle);
    const bgName = styleId === 'india' ? 'India' : styleId;
    await this.persistSettings({
      Background: bgName,
      background: bgName
    });
  }

  async onNotificationToggle(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.notificationsEnabled.set(isChecked);
    await this.persistSettings({
      Notification: isChecked ? 'enabled' : 'disabled',
      notification: isChecked ? 'enabled' : 'disabled'
    });
    this.notificationService.info(`Notifications ${isChecked ? 'enabled' : 'disabled'}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async persistSettings(partial: Record<string, any>) {
    const user = this.authService.user();
    if (!user) return;
    try {
      const currentTheme = this.themeService.theme() || 'light';
      const currentBg = this.themeService.backgroundStyle() === 'india' ? 'India' : (this.themeService.backgroundStyle() || 'India');
      const currentNotif = this.notificationsEnabled() ? 'enabled' : 'disabled';
      const currentLang = this.selectedLanguage() || 'English';

      const dataToSave = {
        Theme: currentTheme,
        Background: currentBg,
        Notification: currentNotif,
        Language: currentLang,
        theme: currentTheme,
        background: currentBg,
        notification: currentNotif,
        language: currentLang,
        updatedAt: serverTimestamp(),
        ...partial
      };

      await setDoc(doc(db, 'accounts', user.uid, 'settings', 'default'), dataToSave, { merge: true });
    } catch (err) {
      console.error('Failed to auto-save settings to Firestore:', err);
    }
  }

  async loadUserSettings(uid: string) {
    try {
      const docRef = doc(db, 'accounts', uid, 'settings', 'default');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const d = snap.data();
        const th = d['Theme'] || d['theme'] || 'light';
        const bg = (d['Background'] || d['background'] || 'India').toLowerCase();
        const notif = (d['Notification'] || d['notification'] || 'enabled') === 'enabled';
        const lang = d['Language'] || d['language'] || 'English';

        this.themeService.setTheme(th);
        this.themeService.setBackgroundStyle(bg);
        this.notificationsEnabled.set(notif);
        this.selectedLanguage.set(lang);
      } else {
        // Initialize default document in Firestore
        await setDoc(docRef, {
          Theme: 'light',
          Background: 'India',
          Notification: 'enabled',
          Language: 'English',
          theme: 'light',
          background: 'India',
          notification: 'enabled',
          language: 'English',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        this.themeService.setTheme('light');
        this.themeService.setBackgroundStyle('india');
        this.notificationsEnabled.set(true);
        this.selectedLanguage.set('English');
      }
    } catch (e) {
      console.error('Error loading default settings:', e);
    }
  }

  toDate(ts: Timestamp | Date | string | number | null | undefined): Date | null {
    if (!ts) return null;
    if (typeof ts === 'object' && 'toDate' in ts && typeof ts.toDate === 'function') {
      return (ts as Timestamp).toDate();
    }
    if (ts instanceof Date) return ts;
    return new Date(ts as string | number);
  }

  onMenuSelect(id: string) {
    this.activeSection.set(id);
    this.scrollToActive(id);
  }

  scrollToActive(id: string) {
    if (!this.isBrowser) return;
    setTimeout(() => {
      const item = document.getElementById('menu-item-' + id);
      if (item) {
        item.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }, 50);
  }

  modalInput = signal('');
  modal = {
    isOpen: signal(false),
    type: signal<'email' | 'password' | 'signout' | 'verify' | 'biometric'>('email'),
    title: signal(''),
    message: signal(''),
    confirmText: signal('')
  };

  menuItems = [
    { id: 'pwa', label: 'App', icon: 'settings', color: '#AF52DE' },
    { id: 'internet', label: 'Internet', icon: 'wifi', color: '#34C759' }
  ];

  safeSearchOptions = [
    { id: 'filter', label: 'Filter explicit results', description: 'Helps filter out explicit text, images, and video.' },
    { id: 'blur', label: 'Blur explicit images', description: 'Blurs explicit images, but explicit text and video may still appear.' },
    { id: 'off', label: 'Off', description: 'Shows all relevant results, even if they are explicit.' }
  ];

  themes = [
    { id: 'light' as const, label: 'Light', icon: 'light_mode' },
    { id: 'dark' as const, label: 'Dark', icon: 'dark_mode' },
    { id: 'system' as const, label: 'System', icon: 'settings_brightness' },
    { id: 'auto' as const, label: 'Auto (Time)', icon: 'schedule' }
  ];

  apiKey = signal<string | null>(null);

  generateApiKey() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
    let key = 'kicgle_live_';
    for(let i=0; i<32; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.apiKey.set(key);
    this.notificationService.success('API Access Key generated. Keep it secret.');
  }

  copyApiKey() {
    const key = this.apiKey();
    if(key) {
        navigator.clipboard.writeText(key);
        this.notificationService.success('API Key copied to clipboard');
    }
  }

  onSave() {
    this.notificationService.success("Settings saved successfully!");
  }

  onClearHistory() {
    this.notificationService.info("Search history cleared.");
  }

  resetSettings() {
    this.themeService.setTheme('system');
    this.notificationService.success("All settings have been reset to defaults.");
  }



  toggleBiometrics(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const checked = checkbox.checked;
    
    // Prevent immediate toggle, we want to show modal first
    checkbox.checked = !checked;
    
    if (checked) {
      this.modal.type.set('biometric');
      this.modal.title.set('Enable Biometrics');
      this.modal.message.set('Secure your account using FaceID or TouchID. This adds an extra layer of protection to your Kicgle identity.\n\nNote: This requires a device with biometric hardware.');
      this.modal.confirmText.set('Set up Biometrics');
      this.modal.isOpen.set(true);
    } else {
      this.biometricEnabled.set(false);
      this.notificationService.info('Biometric login disabled.');
    }
  }



  openConfirmModal(type: 'email' | 'password' | 'signout' | 'verify') {
    this.modal.type.set(type);
    this.modalInput.set('');
    
    switch (type) {
      case 'email':
        this.modal.title.set('Change Email');
        this.modal.message.set('Enter your new email address below. You will need to verify the new email to complete the change.');
        this.modal.confirmText.set('Change Email');
        break;
      case 'password':
        this.modal.title.set('Reset Password');
        this.modal.message.set('We will send a password reset link to your current email address. Are you sure you want to proceed?');
        this.modal.confirmText.set('Send Link');
        break;
      case 'signout':
        this.modal.title.set('Sign Out');
        this.modal.message.set('Are you sure you want to sign out of your Kicgle account? You will need to sign in again to access your settings.');
        this.modal.confirmText.set('Sign Out');
        break;
      case 'verify':
        this.modal.title.set('Verify Email');
        this.modal.message.set('We will send a verification link to your email address. Please check your inbox and follow the instructions.');
        this.modal.confirmText.set('Send Link');
        break;
    }
    
    this.modal.isOpen.set(true);
  }

  isPulseChecking = signal(false);

  pulseCheck() {
    if (this.isPulseChecking()) return;
    this.isPulseChecking.set(true);
    this.pwaService.checkForUpdates();
    setTimeout(() => {
      this.isPulseChecking.set(false);
      this.notificationService.success('Pulse check complete: Network stable.');
    }, 2500);
  }

  closeModal() {
    this.modal.isOpen.set(false);
  }

  async confirmAction() {
    const type = this.modal.type();
    
    if (type === 'biometric') {
      this.isScanning.set(true);
      
      try {
        // Real WebAuthn check if available
        if (this.isBrowser && window.PublicKeyCredential) {
          const isAvailable = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          if (!isAvailable) {
            throw new Error("Biometric hardware not found or not supported on this device.");
          }
          
          // Simulate the actual biometric prompt delay
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          this.biometricEnabled.set(true);
          this.notificationService.success('Biometric authentication enabled successfully!');
        } else {
          // Fallback for browsers without WebAuthn
          await new Promise(resolve => setTimeout(resolve, 2000));
          this.biometricEnabled.set(true);
          this.notificationService.success('Biometric login simulated (Hardware not detected).');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Biometric setup failed.";
        this.notificationService.error(message);
        this.biometricEnabled.set(false);
      } finally {
        this.isScanning.set(false);
        this.closeModal();
      }
      return;
    }

    this.closeModal();

    try {
      if (type === 'email') {
        const emailValue = this.modalInput();
        if (!emailValue || emailValue === this.authService.user()?.email) {
          this.notificationService.error("Please enter a different email address.");
          return;
        }
        await this.authService.updateEmail(emailValue);
        this.notificationService.success("Email address updated! Please verify your new email.");
      } else if (type === 'password') {
        const email = this.authService.user()?.email;
        if (email) {
          await this.authService.resetPassword(email);
          this.notificationService.info("Password reset email sent! Please check your inbox.");
        }

      } else if (type === 'signout') {
        await this.authService.signOut();
        this.notificationService.info("Signed out successfully.");
      } else if (type === 'verify') {
        await this.authService.sendVerificationEmail();
        this.notificationService.info("Verification email sent! Please check your inbox.");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Action failed. Please try again.";
      this.notificationService.error(message);
    }
  }
}
